import { ref, uploadBytes, type FirebaseStorage } from 'firebase/storage'
import type { Functions } from 'firebase/functions'
import { api } from './api'
import { getErrorCode } from './errorCode'
import { logger } from './logger'
import { markReportedToSentry } from './reportedErrors'
import { getFileMetadata } from './shared/getContentDisposition'

const UPLOAD_FEATURE_TAG = 'company-files'

/**
 * True for events reported by `uploadCompanyFile`. The global Sentry filter
 * drops `storage/retry-limit-exceeded` and bare `internal` callable errors as
 * network noise, but here a failed upload or cleanup can leave a file record
 * without a stored file, so keep those events.
 */
export function isCompanyFileUploadEvent(event: {
  tags?: Record<string, unknown>
}) {
  return event.tags?.feature === UPLOAD_FEATURE_TAG
}

interface UploadCompanyFileParams {
  storage: FirebaseStorage
  functions: Functions
  documentsType: string
  companyUid: string
  file: File
  fileType: string
  yearMonth?: string | null
  source: 'add' | 'replace'
}

export async function uploadCompanyFile({
  storage,
  functions,
  documentsType,
  companyUid,
  file,
  fileType,
  yearMonth,
  source,
}: UploadCompanyFileParams) {
  const result = (await api(functions, 'company-files/import', {
    companyUid,
    fileName: file.name,
    fileType,
    ...(yearMonth !== undefined && { yearMonth }),
  })) as { uid?: string } | null

  if (!result?.uid) {
    throw new Error('Server did not return a valid file ID')
  }

  try {
    const fileRef = ref(
      storage,
      `${documentsType}/${companyUid}/${companyUid}/${result.uid}`
    )
    await uploadBytes(fileRef, file, getFileMetadata(file.name, file.type))
  } catch (uploadError) {
    // No file name: it can contain employee names.
    const context = {
      companyUid,
      fileUid: result.uid,
      documentsType,
      fileSize: file.size,
      contentType: file.type,
    }
    logger.captureException(
      uploadError as Error,
      { ...context, errorCode: getErrorCode(uploadError) },
      { feature: UPLOAD_FEATURE_TAG, operation: 'upload', source }
    )
    markReportedToSentry(uploadError)
    try {
      await api(functions, 'documents/delete', {
        documentsType,
        documentUids: [result.uid],
      })
    } catch (cleanupError) {
      logger.captureException(
        cleanupError as Error,
        { ...context, errorCode: getErrorCode(cleanupError) },
        { feature: UPLOAD_FEATURE_TAG, operation: 'upload-cleanup', source }
      )
    }
    throw uploadError
  }
}
